import React, {useCallback, useEffect, useRef, useState} from 'react'
import styled from '@emotion/styled'
import {keyframes} from '@emotion/react'
import {useSelector} from 'react-redux'
import {
  selectProposalInfo,
  selectProposalCountry,
} from '../../../../app-store/ProposalSlice'
import Icon from '../../../../core/shared/Icon'
import {
  PTEyeCrossedSVG,
  PTCaretUpSVG,
  PTCaretDownSVG,
  PTThryvLogo,
  PTCheckSVG,
  marketingCenterIconv1,
  businessCenterIconv1,
  reportingCenterIconv1,
  workforceCenterIconv1,
  seoKeywordsIcon,
  seoPowerboostIcon,
  ThryvSocialPostsIcon,
  thryvLeadsIcon,
  listingsAppIcon,
  thryvGrowthPlatformIcon,
} from '../../../../assets'
import {ProductIcon} from '../../../../core/shared/ProductIcon'
import {ProposalCardTop} from '../ProposalCardTop'
import {
  PreviewOverlay,
  PreviewWrapper,
  PreviewTopBarWrap,
  TopBar,
  TitleSection,
  TitleIconWrap,
  TitleTextBlock,
  PageTitle,
  PageSubtitle,
  PreviewActionGroup,
  ExportPDFBtn,
  HidePreviewBtn,
  PreviewHero,
  PreviewHeroMeta,
  PreviewHeroTitle,
  PreviewHeroDisclaimer,
  CardsScrollArea,
  ScrollArrow,
  CardsContainer,
  CardWrapper,
  FeaturedBanner,
  CardBorderWrap,
  OptionCard,
  CardBody,
  GroupSection,
  GroupHeader,
  GroupArrow,
  GroupName,
  GroupTotal,
  ProductItem,
  ProductIconWrap,
  ProductInfo,
  ProductName,
  ProductSubRow,
  ProductPriceText,
  ViewDetailsLink,
  IncludedText,
  ProductTotal,
  GroupBody,
  ProductDetails,
  DetailRow,
  DetailLabel,
  DetailValue,
  DetailDivider,
  AddonRow,
  SelectBtn,
  CouponSection,
  CouponSectionTitle,
  CouponRow,
  CouponTag,
  CouponDiscount,
  SavingsRow,
} from './styled.components'
import {MC_2_0_PLANS} from '../../../../utils/constants'
import {THRYV_BASIC_CODES} from '../../../../utils/tgp-catalog-flags'
import {getMonthlyRecurringTotal} from '../../utils/proposalTotals'
import {getProductIcon} from '../../../../core/shared/ProductIcons'

const spinAnim = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const ButtonSpinner = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-top-color: transparent;
  animation: ${spinAnim} 0.6s linear infinite;
  flex-shrink: 0;
`

const PricingLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
`

const PricingSpinner = styled.span`
  display: inline-block;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid #e5e7eb;
  border-top-color: #ff5b36;
  animation: ${spinAnim} 0.7s linear infinite;
`

const PRODUCT_ICONS = {
  ma: PTThryvLogo,
  mc: marketingCenterIconv1,
  bc: businessCenterIconv1,
  rc: reportingCenterIconv1,
  wc: workforceCenterIconv1,
  seo: seoKeywordsIcon,
  seobase: seoKeywordsIcon,
  seombg: seoKeywordsIcon,
  seokey: seoKeywordsIcon,
  seopb: seoPowerboostIcon,
  sp: ThryvSocialPostsIcon,
  socialcontentgen: ThryvSocialPostsIcon,
  tl: thryvLeadsIcon,
  appmarket: listingsAppIcon,
  // Thryv Growth Platform plans (Starter / Signature / Premier monthly & annual)
  thryv_gp: thryvGrowthPlatformIcon,
  signature: thryvGrowthPlatformIcon,
  starter: thryvGrowthPlatformIcon,
  premier: thryvGrowthPlatformIcon,
}

const ICON_BG = {
  ma: '#fff3ec',
  mc: '#fef3f2',
  bc: '#eff6ff',
  rc: '#f0fdf4',
  wc: '#fdf4ff',
  seo: '#f9fafb',
  seobase: '#f9fafb',
  seombg: '#f9fafb',
  seokey: '#f9fafb',
  seopb: '#f9fafb',
  sp: '#fff7ed',
  socialcontentgen: '#fff7ed',
  tl: '#fff3ec',
  appmarket: '#f0fdf4',
  thryv_gp: '#f0f4ff',
  signature: '#f0f4ff',
  starter: '#f0f4ff',
  premier: '#f0f4ff',
}

// Thryv Growth Platform monthly plan code prefixes — must be checked before
// the generic mc_ split so they get the GP icon instead of the MC icon.
const THRYV_GP_MC_PREFIXES = ['mc_starter', 'mc_prov2', 'mc_premier']

// For addon-N-{key}-... patterns, skip center prefix and numeric segments to
// find the meaningful product key. For mc_starter-* style, use the _ prefix.
function getIconKey(productCode) {
  if (!productCode) return ''
  if (THRYV_GP_MC_PREFIXES.some(p => productCode.startsWith(p)))
    return 'thryv_gp'
  if (productCode.includes('_')) return productCode.split('_')[0]
  const parts = productCode.split('-')
  if (parts[0] !== 'addon' && parts[0] !== 'ent') return parts[0]
  const SKIP = new Set(['mc', 'bc', 'rc', 'wc', 'ot', 'm2m', 'thryv'])
  for (let i = 2; i < parts.length; i++) {
    if (!SKIP.has(parts[i]) && !/^\d+$/.test(parts[i])) return parts[i]
  }
  return parts[0]
}

const GROUP_LABEL = {
  CENTER: 'Software',
  APP: 'Add-Ons',
  ONE_TIME_FEE: 'One-Time Fees',
}

const GROUP_ORDER = {CENTER: 0, APP: 1, ONE_TIME_FEE: 2}

// Mirrors legacy cart-item.js's showAddonPrice / OrderSummary's LEADS_LIFT_BNI_CODE
// exception: Leads Lift BNI is a real standalone add-on that can legitimately be
// priced $0, so it always shows its actual price instead of "Included".
// const LEADS_LIFT_BNI_CODE = 'addon-5-leadslift-bni-m2m'

const DETAIL_LABELS = {
  domain: 'Domain',
  onboarding: 'Optional Onboarding',
  eaid: 'EAID',
  targetUrl: 'Target URL',
  location: 'Location',
  budget: 'Budget',
  phoneNumber: 'Phone',
  includeOnboarding: 'Include Onboarding',
}

const INTERNAL_CONFIG_KEYS = []

const COUNTRY_LOCALE = {
  US: {locale: 'en-US', currency: 'USD'},
  AU: {locale: 'en-AU', currency: 'AUD'},
  CA: {locale: 'en-CA', currency: 'CAD'},
  NZ: {locale: 'en-NZ', currency: 'NZD'},
  GB: {locale: 'en-GB', currency: 'GBP'},
}

function formatCurrency(amount, country) {
  const n = Number(amount) || 0
  const {locale, currency} = COUNTRY_LOCALE[country] ?? COUNTRY_LOCALE.US
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n)
}

function parseConfig(raw) {
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
}

function billingLabel(freq) {
  if (freq === 'monthly') return '/mo'
  if (freq === 'annual') return '/yr'
  return ''
}

function groupByType(items) {
  const map = {}
  items.forEach(item => {
    const key = item.productType || 'Other'
    if (!map[key]) map[key] = []
    map[key].push(item)
  })
  return Object.entries(map).map(([name, list]) => ({name, items: list}))
}

function ProposalPreview({
  onBack,
  overlay = false,
  proposal: proposalProp,
  onOptionSelect,
  client = false,
  selectingOptionId = null,
  pricingLoading = false,
}) {
  const proposalInfo = useSelector(selectProposalInfo)
  const country = useSelector(selectProposalCountry) ?? 'US'
  const activeProposal = proposalProp || proposalInfo
  // const {clientType} = activeProposal
  const options = (activeProposal.options || [])
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)

  const scrollable = options.length > 3

  // proposal_options.is_selected — set by the accept flow once the client picks
  // an option, so the dashboard preview can show the rep which one they went
  // with. Rep-facing only: the client-facing page never highlights a choice
  // back at the client who made it.
  const selectedOptionId = client
    ? null
    : options.find(opt => opt.isSelected)?.optionId ?? null

  const [openGroups, setOpenGroups] = useState(() => {
    const init = {}
    options.forEach(opt => {
      groupByType(opt.items || []).forEach(g => {
        init[`${opt.optionId}-${g.name}`] = true
      })
    })
    return init
  })

  const toggleGroup = key =>
    setOpenGroups(prev => ({...prev, [key]: !prev[key]}))

  const [openDetails, setOpenDetails] = useState({})
  const toggleDetail = key =>
    setOpenDetails(prev => ({...prev, [key]: !prev[key]}))

  const scrollRef = useRef(null)
  const [canLeft, setCanLeft] = useState(false)
  const [canRight, setCanRight] = useState(false)
  const dragRef = useRef({active: false, startX: 0, scrollLeft: 0})

  const updateArrows = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanLeft(el.scrollLeft > 4)
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)
  }, [])

  useEffect(() => {
    if (!scrollable) return
    updateArrows()
    const el = scrollRef.current
    el?.addEventListener('scroll', updateArrows, {passive: true})
    const ro = new ResizeObserver(updateArrows)
    if (el) ro.observe(el)
    return () => {
      el?.removeEventListener('scroll', updateArrows)
      ro.disconnect()
    }
  }, [scrollable, updateArrows])

  useEffect(() => {
    if (!scrollable) return
    const onMove = e => {
      if (!dragRef.current.active || !scrollRef.current) return
      const dx = e.clientX - dragRef.current.startX
      scrollRef.current.scrollLeft = dragRef.current.scrollLeft - dx
    }
    const onUp = () => {
      if (!dragRef.current.active || !scrollRef.current) return
      dragRef.current.active = false
      const el = scrollRef.current
      el.style.cursor = ''
      el.style.userSelect = ''
      el.style.scrollSnapType = 'x mandatory'
      updateArrows()
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [scrollable, updateArrows])

  // Bring the client's chosen option into view when the cards overflow, so it
  // isn't hidden off to the right when the preview opens.
  const selectedCardRef = useRef(null)
  useEffect(() => {
    if (!scrollable || !selectedOptionId) return
    selectedCardRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  }, [scrollable, selectedOptionId])

  const onDragStart = useCallback(e => e.preventDefault(), [])

  const onMouseDown = useCallback(
    e => {
      if (!scrollable || !scrollRef.current) return
      const el = scrollRef.current
      dragRef.current = {
        active: true,
        startX: e.clientX,
        scrollLeft: el.scrollLeft,
      }
      el.style.cursor = 'grabbing'
      el.style.userSelect = 'none'
      el.style.scrollSnapType = 'none'
    },
    [scrollable],
  )

  const scroll = dir => {
    scrollRef.current?.scrollBy({left: dir * 340, behavior: 'smooth'})
  }

  const [exportingPDF, setExportingPDF] = useState(false)

  const handleExportPDF = async () => {
    if (exportingPDF || !scrollRef.current) return
    setExportingPDF(true)
    try {
      const html2canvas = (await import('html2canvas')).default
      const jsPDFMod = await import('jspdf')
      const jsPDF = jsPDFMod.jsPDF ?? jsPDFMod.default
      const {flushSync} = await import('react-dom')

      // Open all detail sections so their DOM nodes exist when we clone
      const allDetailKeys = {}
      options.forEach(opt => {
        ;(opt.items || []).forEach((item, i) => {
          const configOpts = parseConfig(item.configuration)
          const configEntries = Object.entries(configOpts).filter(
            ([k, v]) =>
              v != null && v !== '' && !INTERNAL_CONFIG_KEYS.includes(k),
          )
          const itemAddons = item.addons || []
          const hasDetails =
            configEntries.length > 0 ||
            (itemAddons.length > 0 &&
              (item.productType === 'CENTER' || item.productType === 'APP'))
          if (hasDetails) {
            allDetailKeys[item.itemId || item.productCode || i] = true
          }
        })
      })
      flushSync(() => setOpenDetails(allDetailKeys))

      // Off-screen printable container (gradient background, matching live preview)
      const printRoot = document.createElement('div')
      Object.assign(printRoot.style, {
        position: 'absolute',
        left: '-9999px',
        top: '0',
        width: '860px',
        background:
          'linear-gradient(179.57deg, #171d37 0.38%, #6474a9 108.67%)',
        padding: '40px',
        fontFamily: 'Open Sans, sans-serif',
        boxSizing: 'border-box',
      })

      // Hero with white text over gradient
      const heroDiv = document.createElement('div')
      heroDiv.style.cssText = 'text-align:center;margin-bottom:28px;'
      heroDiv.innerHTML = `
        <p style="font-size:11px;font-weight:400;letter-spacing:0.8px;text-transform:uppercase;color:rgba(255,255,255,0.6);margin:0 0 8px;">YOUR PERSONALIZED PROPOSAL</p>
        <h2 style="font-size:22px;font-weight:700;color:#ffffff;line-height:1.2;letter-spacing:-0.3px;margin:0 0 8px;">
          ${
            proposalInfo.businessName
              ? `Here are your options for ${proposalInfo.businessName}.`
              : 'Here are your options.'
          }
        </h2>
        <p style="font-size:12px;font-style:italic;color:rgba(255,255,255,0.5);margin:0;">
          *Pricing is subject to change. Final pricing will be presented during checkout.
        </p>
      `
      printRoot.appendChild(heroDiv)

      // Clone cards container → multi-column layout for PDF
      const colsPerRow = options.length <= 3 ? options.length : 2
      const gapPx = 20
      const cardWidthPct = `calc(${100 / colsPerRow}% - ${
        ((colsPerRow - 1) * gapPx) / colsPerRow
      }px)`

      const cardsClone = scrollRef.current.cloneNode(true)
      Object.assign(cardsClone.style, {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: `${gapPx}px`,
        overflow: 'visible',
        width: '100%',
        cursor: 'default',
      })
      Array.from(cardsClone.children).forEach(child => {
        Object.assign(child.style, {
          flex: `0 0 ${cardWidthPct}`,
          width: cardWidthPct,
          maxWidth: `min(${cardWidthPct}, 450px)`,
          marginTop: '0', // reset featured card negative margin
          scrollSnapAlign: 'none',
        })
      })
      printRoot.appendChild(cardsClone)

      // Restore open state (non-blocking — happens after clone is done)
      setOpenDetails({})

      // Hide interactive "View details" toggles — not useful in a PDF
      cardsClone.querySelectorAll('[data-pdf-hide]').forEach(el => {
        el.style.display = 'none'
      })

      document.body.appendChild(printRoot)

      // Remove fixed heights / overflow:hidden that clip content in PDF
      // (GroupBody has height:26rem + overflow-y:auto for in-app scrolling)
      printRoot.querySelectorAll('*').forEach(el => {
        const s = window.getComputedStyle(el)
        if (
          (s.overflowY === 'auto' ||
            s.overflowY === 'scroll' ||
            s.overflow === 'hidden') &&
          s.height !== 'auto'
        ) {
          el.style.height = 'auto'
          el.style.maxHeight = 'none'
          el.style.overflow = 'visible'
          el.style.overflowY = 'visible'
        }
      })

      // Fix text truncation — must run AFTER DOM insertion so getComputedStyle works
      printRoot.querySelectorAll('*').forEach(el => {
        const s = window.getComputedStyle(el)
        if (s.textOverflow === 'ellipsis' || s.whiteSpace === 'nowrap') {
          el.style.textOverflow = 'clip'
          el.style.whiteSpace = 'normal'
          el.style.overflow = 'visible'
        }
      })

      const pdf = new jsPDF('p', 'mm', 'a4')
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()

      // Round printRoot's height up to a whole number of A4 pages before
      // capture, so its own CSS gradient — not a separately reconstructed
      // one — covers every page fully. Avoids any seam between a real and
      // an approximated gradient at page boundaries.
      const pageHeightPxInRoot =
        printRoot.getBoundingClientRect().width * (pageHeight / pageWidth)
      const numPages = Math.max(
        1,
        Math.ceil(printRoot.offsetHeight / pageHeightPxInRoot),
      )
      printRoot.style.minHeight = `${numPages * pageHeightPxInRoot}px`

      const canvas = await html2canvas(printRoot, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
        logging: false,
      })
      document.body.removeChild(printRoot)

      const totalHeightMm = canvas.height * (pageWidth / canvas.width)

      // Tolerance absorbs sub-pixel rounding from the minHeight/capture
      // round-trip so it can't manufacture a stray near-empty trailing page.
      let yOffset = 0
      while (yOffset < totalHeightMm - 0.5) {
        if (yOffset > 0) pdf.addPage()
        pdf.addImage(
          canvas.toDataURL('image/png'),
          'PNG',
          0,
          -yOffset,
          pageWidth,
          totalHeightMm,
        )
        yOffset += pageHeight
      }

      const safeName = (proposalInfo.businessName || 'proposal')
        .replace(/[^a-z0-9]/gi, '-')
        .toLowerCase()
      pdf.save(`${safeName}-proposal.pdf`)
    } catch (err) {
      console.error('[ProposalPreview] PDF export failed:', err)
    } finally {
      setExportingPDF(false)
    }
  }

  const content = (
    <PreviewWrapper>
      {onBack && (
        <PreviewTopBarWrap>
          <TopBar>
            <TitleSection>
              <TitleIconWrap>
                <Icon name="product-catalog" size="md" />
              </TitleIconWrap>
              <TitleTextBlock>
                <PageTitle>Preview Proposals</PageTitle>
                <PageSubtitle>
                  This is exactly what your client will see
                </PageSubtitle>
              </TitleTextBlock>
            </TitleSection>
            <PreviewActionGroup>
              <ExportPDFBtn
                type="button"
                onClick={handleExportPDF}
                disabled={exportingPDF}
              >
                {exportingPDF ? (
                  <ButtonSpinner />
                ) : (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M7 9.917L3.5 6.417h2.333V1.167h2.334v5.25H10.5L7 9.917Z" />
                    <path d="M1.167 11.083h11.666V12.25H1.167v-1.167Z" />
                  </svg>
                )}
                {exportingPDF ? 'Exporting...' : 'Export to PDF'}
              </ExportPDFBtn>
              <HidePreviewBtn type="button" onClick={onBack}>
                <PTEyeCrossedSVG width="14" height="14" fill="currentColor" />
                Hide Preview
              </HidePreviewBtn>
            </PreviewActionGroup>
          </TopBar>
        </PreviewTopBarWrap>
      )}

      {!client && (
        <PreviewHero>
          <PreviewHeroMeta>Your Personalized Proposal</PreviewHeroMeta>
          <PreviewHeroTitle>
            {proposalInfo.businessName
              ? `Hi ${proposalInfo.businessName}, here are your options for ${proposalInfo.businessName}.`
              : 'Here are your options.'}
          </PreviewHeroTitle>
          <PreviewHeroDisclaimer>
            *Pricing is subject to change. Final pricing will be presented
            during checkout.
          </PreviewHeroDisclaimer>
        </PreviewHero>
      )}

      <div
        style={{
          position: 'relative',
          ...(client ? {} : {padding: '0 24px 40px'}),
        }}
      >
        {pricingLoading && (
          <PricingLoadingOverlay
            style={{
              position: client ? 'fixed' : 'absolute',
              background: client
                ? 'rgba(23, 29, 55, 0.7)'
                : 'rgba(255, 255, 255, 0.75)',
            }}
          >
            <PricingSpinner
              style={{
                borderColor: client ? 'rgba(255, 255, 255, 0.25)' : '#e5e7eb',
                borderTopColor: client ? '#ffffff' : '#ff5b36',
              }}
            />
          </PricingLoadingOverlay>
        )}
        <CardsScrollArea>
          {scrollable && (
            <ScrollArrow
              dir="left"
              visible={canLeft}
              onClick={() => scroll(-1)}
              aria-label="Scroll left"
            >
              <PTCaretUpSVG
                width="10"
                height="10"
                fill="currentColor"
                style={{transform: 'rotate(-90deg)'}}
              />
            </ScrollArrow>
          )}
          <CardsContainer
            ref={scrollRef}
            scrollable={scrollable}
            onMouseDown={onMouseDown}
            onDragStart={onDragStart}
          >
            {options
              .filter(opt => opt.isSent || !client)
              .map((option, idx) => {
                // Cross-group displacement: MC Foundation and MC 2.0 plans are
                // mutually exclusive even though they live in different uigroups.
                // Compute a set of productCodes that are displaced by a newer
                // selection, so they are excluded from all totals and display.
                const ALL_MC_CODES = new Set([
                  'mc_plus-5-thryv-m2m',
                  'mc_pro-5-thryv-m2m',
                  'mc_pro_enh-5-thryv-m2m',
                  'mc_foundation-5-thryv-m2m',
                  ...THRYV_BASIC_CODES,
                  ...MC_2_0_PLANS,
                ])
                const newItemCodes = new Set(
                  (option.items || [])
                    .filter(i => !i.alreadyIncluded)
                    .map(i => i.productCode),
                )
                const hasNewMCPlan = [...newItemCodes].some(c =>
                  ALL_MC_CODES.has(c),
                )
                // All MC plan families are mutually exclusive — whichever is newly
                // added displaces every other MC variant that the client already has.
                const isDisplaced = code =>
                  hasNewMCPlan &&
                  ALL_MC_CODES.has(code) &&
                  !newItemCodes.has(code)

                const sorted = (option.items || [])
                  .filter(item => !isDisplaced(item.productCode))
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                const groups = groupByType(sorted).sort(
                  (a, b) =>
                    (GROUP_ORDER[a.name] ?? 99) - (GROUP_ORDER[b.name] ?? 99),
                )
                const starCount = idx + 1
                // FULSAS-14570: monthly recurring charges only (no one-time
                // fees, no annual plans) — see getMonthlyRecurringTotal.
                const effectiveMonthlyTotal = getMonthlyRecurringTotal(
                  option.items || [],
                  {isDisplaced},
                )
                const effectiveDueTodayTotal = (option.items || []).reduce(
                  (sum, item) => {
                    if (item.alreadyIncluded || isDisplaced(item.productCode))
                      return sum
                    return (
                      sum +
                      Number(
                        item.dueTodayAmount ??
                          item.recurringAmount ??
                          item.price ??
                          item.unitPrice ??
                          0,
                      ) +
                      (item.addons || []).reduce(
                        (aSum, addon) =>
                          addon.alreadyIncluded
                            ? aSum
                            : aSum +
                              Number(
                                addon.dueTodayAmount ??
                                  addon.recurringAmount ??
                                  addon.unitPrice ??
                                  0,
                              ),
                        0,
                      )
                    )
                  },
                  0,
                )
                const totalSavings = Math.max(
                  effectiveMonthlyTotal - effectiveDueTodayTotal,
                  0,
                )
                // Backend-persisted coupons use { couponCode, couponName } with no
                // isValid field; treat absent isValid as truthy (they were valid at
                // save time). Builder-session coupons use { code, isValid }.
                const validCoupons = (option.coupons ?? []).filter(
                  c => c.isValid !== false,
                )
                const isClientSelected = option.optionId === selectedOptionId
                return (
                  <CardWrapper
                    key={option.optionId}
                    ref={isClientSelected ? selectedCardRef : undefined}
                    scrollable={scrollable}
                    client={client}
                    clientSelected={isClientSelected}
                  >
                    {isClientSelected && (
                      <FeaturedBanner>Client selected</FeaturedBanner>
                    )}
                    <CardBorderWrap>
                      <OptionCard>
                        <ProposalCardTop
                          idx={idx}
                          optionName={option.optionName}
                          starCount={starCount}
                          totalStars={options.length}
                          dueTodayTotal={effectiveDueTodayTotal}
                          monthlyTotal={effectiveMonthlyTotal}
                          actionBtn={
                            onOptionSelect ? (
                              <SelectBtn
                                type="button"
                                onClick={() => onOptionSelect(option.optionId)}
                                style={{fontSize: '10px'}}
                                disabled={!!selectingOptionId}
                              >
                                {selectingOptionId === option.optionId ? (
                                  <>
                                    <ButtonSpinner />
                                    Selecting...
                                  </>
                                ) : (
                                  <>
                                    <PTCheckSVG width="14" height="14" />
                                    Select Proposal
                                  </>
                                )}
                              </SelectBtn>
                            ) : undefined
                          }
                        />

                        <CardBody>
                          <GroupBody>
                            {groups.map(group => {
                              const key = `${option.optionId}-${group.name}-${idx}`
                              const isOpen = openGroups[key] !== false
                              const label =
                                GROUP_LABEL[group.name] || group.name
                              const count = String(group.items.length).padStart(
                                2,
                                '0',
                              )
                              const groupTotal = group.items.reduce(
                                (sum, item) => {
                                  if (
                                    item.alreadyIncluded ||
                                    isDisplaced(item.productCode)
                                  )
                                    return sum
                                  return (
                                    sum +
                                    Number(
                                      item.dueTodayAmount ??
                                        item.recurringAmount ??
                                        item.price ??
                                        item.unitPrice ??
                                        0,
                                    ) +
                                    (item.addons || []).reduce(
                                      (aSum, addon) =>
                                        addon.alreadyIncluded
                                          ? aSum
                                          : aSum +
                                            Number(
                                              addon.dueTodayAmount ??
                                                addon.recurringAmount ??
                                                addon.unitPrice ??
                                                0,
                                            ),
                                      0,
                                    )
                                  )
                                },
                                0,
                              )

                              return (
                                <GroupSection key={key}>
                                  <GroupHeader onClick={() => toggleGroup(key)}>
                                    <GroupArrow>
                                      {isOpen ? (
                                        <PTCaretUpSVG
                                          width="10"
                                          height="10"
                                          fill="currentColor"
                                        />
                                      ) : (
                                        <PTCaretDownSVG
                                          width="10"
                                          height="10"
                                          fill="currentColor"
                                        />
                                      )}
                                    </GroupArrow>
                                    <GroupName>
                                      {label} - {count} Items
                                    </GroupName>
                                    <GroupTotal>
                                      {formatCurrency(groupTotal, country)}
                                    </GroupTotal>
                                  </GroupHeader>

                                  {isOpen &&
                                    group.items.map((item, i) => {
                                      const itemPrice = Number(
                                        item.recurringAmount ??
                                          item.price ??
                                          item.unitPrice ??
                                          0,
                                      )
                                      const hasCost = itemPrice > 0
                                      const itemTotal = item.alreadyIncluded
                                        ? 0
                                        : item.dueTodayAmount != null
                                        ? Number(item.dueTodayAmount)
                                        : itemPrice
                                      const iconKey = getIconKey(
                                        item.productCode,
                                      )
                                      const icon =
                                        PRODUCT_ICONS[iconKey] ??
                                        getProductIcon(item.productName)
                                      const bg = ICON_BG[iconKey] ?? '#f3f4f6'
                                      const configOpts = parseConfig(
                                        item.configuration,
                                      )
                                      const configEntries = Object.entries(
                                        configOpts,
                                      ).filter(
                                        ([k, v]) =>
                                          v != null &&
                                          v !== '' &&
                                          !INTERNAL_CONFIG_KEYS.includes(k),
                                      )
                                      const itemAddons = item.addons || []
                                      const hasDetails =
                                        configEntries.length > 0 ||
                                        (itemAddons.length > 0 &&
                                          (item.productType === 'CENTER' ||
                                            item.productType === 'APP'))
                                      const detailKey =
                                        item.itemId || item.productCode || i
                                      const isDetailOpen =
                                        !!openDetails[detailKey]

                                      return (
                                        <React.Fragment
                                          key={
                                            item.itemId || item.productCode || i
                                          }
                                        >
                                          <ProductItem highlighted={hasCost}>
                                            {icon ? (
                                              <ProductIconWrap bg={bg}>
                                                <img
                                                  src={icon}
                                                  alt={item.productName}
                                                />
                                              </ProductIconWrap>
                                            ) : (
                                              <ProductIcon
                                                productName={item.productName}
                                                size={36}
                                              />
                                            )}
                                            <ProductInfo>
                                              <ProductName
                                                highlighted={hasCost}
                                              >
                                                {item.productName}
                                              </ProductName>
                                              {itemPrice > 0 ? (
                                                <ProductSubRow>
                                                  <ProductPriceText>
                                                    {formatCurrency(
                                                      itemPrice,
                                                      country,
                                                    )}
                                                    {billingLabel(
                                                      item.billingFrequency,
                                                    )}
                                                  </ProductPriceText>
                                                  {hasDetails && (
                                                    <ViewDetailsLink
                                                      data-pdf-hide="true"
                                                      onClick={() =>
                                                        toggleDetail(detailKey)
                                                      }
                                                    >
                                                      View details{' '}
                                                      {isDetailOpen ? (
                                                        <PTCaretUpSVG
                                                          width="8"
                                                          height="8"
                                                          fill="currentColor"
                                                        />
                                                      ) : (
                                                        <PTCaretDownSVG
                                                          width="8"
                                                          height="8"
                                                          fill="currentColor"
                                                        />
                                                      )}
                                                    </ViewDetailsLink>
                                                  )}
                                                </ProductSubRow>
                                              ) : (
                                                <IncludedText>
                                                  Included
                                                </IncludedText>
                                              )}
                                            </ProductInfo>
                                            <ProductTotal>
                                              {formatCurrency(
                                                itemTotal,
                                                country,
                                              )}
                                            </ProductTotal>
                                          </ProductItem>
                                          {isDetailOpen && hasDetails && (
                                            <ProductDetails>
                                              {configEntries.map(([k, v]) => (
                                                <DetailRow key={k}>
                                                  <DetailLabel>
                                                    {DETAIL_LABELS[k] || k}
                                                  </DetailLabel>
                                                  <DetailValue>
                                                    {k === 'onboarding'
                                                      ? v === 'yes'
                                                        ? 'Opted-In'
                                                        : 'Opted-Out'
                                                      : typeof v === 'object' &&
                                                        v !== null
                                                      ? String(
                                                          v.value ??
                                                            JSON.stringify(v),
                                                        )
                                                      : String(v)}
                                                  </DetailValue>
                                                </DetailRow>
                                              ))}
                                              {itemAddons.length > 0 &&
                                                (item.productType ===
                                                  'CENTER' ||
                                                  item.productType ===
                                                    'APP') && (
                                                  <>
                                                    <DetailDivider>
                                                      {item.productType ===
                                                      'CENTER'
                                                        ? 'Entitlements'
                                                        : 'SEO Add-Ons'}
                                                    </DetailDivider>
                                                    {itemAddons.map(
                                                      (addon, ai) => (
                                                        <AddonRow
                                                          key={
                                                            addon.code ||
                                                            addon.productCode ||
                                                            ai
                                                          }
                                                        >
                                                          <span>
                                                            {addon.name ||
                                                              addon.productName}
                                                          </span>
                                                          <span>
                                                            {Number(
                                                              addon.dueTodayAmount ??
                                                                addon.recurringAmount ??
                                                                addon.unitPrice ??
                                                                0,
                                                            ) > 0
                                                              ? `${formatCurrency(
                                                                  addon.dueTodayAmount ??
                                                                    addon.recurringAmount ??
                                                                    addon.unitPrice,
                                                                  country,
                                                                )}${billingLabel(
                                                                  addon.billingFrequency,
                                                                )}`
                                                              : 'Included'}
                                                          </span>
                                                        </AddonRow>
                                                      ),
                                                    )}
                                                  </>
                                                )}
                                            </ProductDetails>
                                          )}
                                        </React.Fragment>
                                      )
                                    })}
                                </GroupSection>
                              )
                            })}
                          </GroupBody>
                          {validCoupons.length > 0 && (
                            <CouponSection>
                              <CouponSectionTitle>Coupons</CouponSectionTitle>
                              {validCoupons.map(coupon => (
                                <CouponRow
                                  key={coupon.code ?? coupon.couponCode}
                                >
                                  <CouponTag>
                                    {coupon.name ??
                                      coupon.couponName ??
                                      coupon.code ??
                                      coupon.couponCode}
                                  </CouponTag>
                                  <CouponDiscount>
                                    {coupon.discountPercent > 0
                                      ? `${coupon.discountPercent}% off`
                                      : coupon.discountAmount > 0
                                      ? `-${formatCurrency(
                                          coupon.discountAmount,
                                          country,
                                        )}`
                                      : 'Applied'}
                                  </CouponDiscount>
                                </CouponRow>
                              ))}
                              {totalSavings > 0 && (
                                <SavingsRow>
                                  <span>Total Savings</span>
                                  <span>
                                    -{formatCurrency(totalSavings, country)}
                                  </span>
                                </SavingsRow>
                              )}
                            </CouponSection>
                          )}
                        </CardBody>
                      </OptionCard>
                    </CardBorderWrap>
                  </CardWrapper>
                )
              })}
          </CardsContainer>
          {scrollable && (
            <ScrollArrow
              dir="right"
              visible={canRight}
              onClick={() => scroll(1)}
              aria-label="Scroll right"
            >
              <PTCaretDownSVG
                width="10"
                height="10"
                fill="currentColor"
                style={{transform: 'rotate(-90deg)'}}
              />
            </ScrollArrow>
          )}
        </CardsScrollArea>
      </div>
    </PreviewWrapper>
  )

  if (overlay) {
    return <PreviewOverlay>{content}</PreviewOverlay>
  }

  return content
}

export {ProposalPreview}
